import type { Edge, Node } from 'reactflow';
import type { FlowDefinition, FlowBlock, FlowEdge } from '../../types/flow';

export interface BlockTypeMeta {
  type: string;
  label: string;
  icon: string;
  color: string;
  defaultData: () => Partial<FlowBlock>;
}

export const BLOCK_TYPES: BlockTypeMeta[] = [
  {
    type: 'agent',
    label: 'Agent',
    icon: '🤖',
    color: '#3b82f6',
    defaultData: () => ({
      type: 'agent',
      role: 'general_assistant',
      label: 'New Agent',
      allowedMethods: [],
    }),
  },
  {
    type: 'function',
    label: 'Function',
    icon: '⚙️',
    color: '#10b981',
    defaultData: () => ({
      type: 'function',
      label: 'New Function',
      method: 'files.read',
      params: {},
    }),
  },
  {
    type: 'router',
    label: 'Router',
    icon: '🔀',
    color: '#f59e0b',
    defaultData: () => ({
      type: 'router',
      label: 'New Router',
      evaluate: { type: 'state', path: '' },
      branches: {},
      default: '',
    }),
  },
  {
    type: 'output',
    label: 'Output',
    icon: '📤',
    color: '#8b5cf6',
    defaultData: () => ({
      type: 'output',
      label: 'Output',
    }),
  },
  {
    type: 'memory',
    label: 'Memory',
    icon: '🧠',
    color: '#ec4899',
    defaultData: () => ({
      type: 'memory',
      label: 'Memory',
      action: 'extract',
      judge: true,
    }),
  },
  {
    type: 'set',
    label: 'Set',
    icon: '📝',
    color: '#6366f1',
    defaultData: () => ({
      type: 'set',
      label: 'Set Values',
      values: { key: '{{state.input}}' },
    }),
  },
  {
    type: 'code',
    label: 'Code',
    icon: '📜',
    color: '#0ea5e9',
    defaultData: () => ({
      type: 'code',
      label: 'Code',
      code: '// inputs = { input, outputs, messages, workingMemory, context }\n// return object to set as output\nreturn { result: inputs.input };',
      timeout: 5000,
    }),
  },
  {
    type: 'compaction',
    label: 'Compaction',
    icon: '🗜️',
    color: '#f97316',
    defaultData: () => ({
      type: 'compaction',
      label: 'Compact Context',
      threshold: 0.75,
      keepRecent: 6,
      extractFacts: true,
    }),
  },
  {
    type: 'transform',
    label: 'Transform',
    icon: '🔄',
    color: '#14b8a6',
    defaultData: () => ({
      type: 'transform',
      label: 'Transform',
      mapping: { result: '{{state.outputs.agent.response}}' },
    }),
  },
];

export function getBlockTypeMeta(type: string): BlockTypeMeta | undefined {
  return BLOCK_TYPES.find((t) => t.type === type);
}

const NODE_BASE_X = 250;
const NODE_SPACING_X = 300;
const NODE_SPACING_Y = 150;

export function flowDefinitionToNodes(flow: FlowDefinition): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = flow.nodes.map((block, i) => {
    const meta = getBlockTypeMeta(block.type);
    return {
      id: block.id,
      type: 'blockNode',
      position: { x: NODE_BASE_X + (i % 3) * NODE_SPACING_X, y: Math.floor(i / 3) * NODE_SPACING_Y },
      data: { ...block, _meta: meta },
    };
  });

  const edges: Edge[] = flow.edges.map((e, i) => ({
    id: `edge-${i}-${e.from}-${e.to}`,
    source: e.from,
    target: e.to,
    label: e.condition || undefined,
    type: e.condition ? 'step' : 'smoothstep',
    animated: false,
    data: { condition: e.condition },
  }));

  return { nodes, edges };
}

export function nodesToFlowDefinition(
  nodes: Node[],
  edges: Edge[],
  meta: { id: string; name: string; description: string; version: string; enabled: boolean; entryNode: string },
): FlowDefinition {
  const flowNodes: FlowBlock[] = nodes.map((n) => {
    const data = { ...n.data };
    delete data._meta;
    return data as unknown as FlowBlock;
  });

  const flowEdges: FlowEdge[] = edges.map((e) => ({
    from: e.source,
    to: e.target,
    condition: e.data?.condition || (e.label ? String(e.label) : undefined),
  }));

  return {
    ...meta,
    entryNode: meta.entryNode || nodes[0]?.id || '',
    nodes: flowNodes,
    edges: flowEdges,
  };
}

export function createNewBlock(type: string): FlowBlock {
  const meta = getBlockTypeMeta(type);
  if (!meta) throw new Error(`Unknown block type: ${type}`);
  const id = `${type}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  const data = meta.defaultData();
  return { id, ...data } as unknown as FlowBlock;
}

export function createNewNode(type: string, position: { x: number; y: number }): Node {
  const block = createNewBlock(type);
  const meta = getBlockTypeMeta(type);
  return {
    id: block.id,
    type: 'blockNode',
    position,
    data: { ...block, _meta: meta },
  };
}